import { Col, message, Row, TablePaginationConfig, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import * as S from './styles';
import {
  GQL_AverageTeacherScoresChart,
  GQL_FetchAverageTeacherScoresInput,
  GQL_TeacherScores,
} from '../../../types/charts';
import { FilterData } from '../../../types/investigation';
import Filter from '../Filter';
import { FiHelpCircle } from 'react-icons/fi';
import { useLazyQuery } from '@apollo/client';
import { gqlSchema } from '../../../gql/schema';
import DetailTable from './DetailTable';
import Button from '../../Button';
import { downloadCsv } from '../../../utils/files';
import Papa from 'papaparse';
import CardAverage from './CardAverage';

const StudentsScores = () => {
  const [pagination, setPagination] = useState<TablePaginationConfig>();
  const [filterData, setFilterData] = useState<FilterData>({ organizations: false, teachers: false });

  const [getAverageTeacherScoresChart, { data, loading }] = useLazyQuery<
    { getAverageTeacherScoresChart: GQL_AverageTeacherScoresChart },
    { data: GQL_FetchAverageTeacherScoresInput }
  >(gqlSchema.ChartSchema.queries.CHARTS.getAverageTeacherScoresChart, {
    onError: (err) => {
      message.error('There was an error loading investigation assignment rate: ' + err.message || 'Unexpected Error');
    },
  });

  useEffect(() => {
    const { organizations, disciplineIds, teachers, ...data } = filterData;
    getAverageTeacherScoresChart({
      variables: {
        data,
      },
    });
  }, [getAverageTeacherScoresChart, filterData]);

  const onChangeFilterData = useCallback((data: FilterData) => {
    setFilterData(data);
  }, []);

  const downloadDetails = useCallback(() => {
    const headers = [
      [
        'First Name',
        'Last Name',
        'Email',
        'Avg. Raw Report Score (out of 38)',
        'Avg. Percentage Report Score',
        'Avg. Engagement Score (out of 5)',
      ],
    ];
    const csvData =
      data?.getAverageTeacherScoresChart?.scoresByTeacher?.data.map((row: GQL_TeacherScores) => {
        return [
          row.teacherFirstName,
          row.teacherLastName,
          row.teacherEmail,
          row.averageReportScores?.toFixed(2) || 0,
          `${Math.round(row.averageReportScoresPercentage * 100)}%` || 0,
          row.averageEngagementScores?.toFixed(2) || 0,
        ];
      }) || [];
    downloadCsv(Papa.unparse([...headers, ...csvData]), `Teachers Scores Average.csv`);
  }, [data]);

  return (
    <>
      <Row>
        <Col span={24} style={{ marginBottom: 10 }}>
          <S.Title>Teachers Scores Average</S.Title>
          <Tooltip title="Average of report and engagement scores assigned to students (by teacher and overall).">
            <FiHelpCircle style={{ marginLeft: 5 }} />
          </Tooltip>
        </Col>
      </Row>
      <Row gutter={[16, 24]}>
        <Col span={18}>
          <Filter
            showGradeFilter={false}
            showSubjectFilter={false}
            showDateRangeFilter={false}
            filterUsers={false}
            onChangeFilterData={onChangeFilterData}
          />
        </Col>
        <Col span={6}>
          <Button text="Download Details" block onClick={downloadDetails} />
        </Col>
        <Col span={24}>
          <Row justify="center" gutter={[16, 24]}>
            <Col span={12}>
              <CardAverage
                title="Average Overall Report Score"
                average={data?.getAverageTeacherScoresChart.averageScores?.reportScore || 0}
              />
            </Col>
            <Col span={12}>
              <CardAverage
                title="Average Overall Engagement Score"
                average={data?.getAverageTeacherScoresChart.averageScores?.engagementScore || 0}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <DetailTable
            loading={loading}
            pagination={pagination}
            setPagination={setPagination}
            data={data?.getAverageTeacherScoresChart?.scoresByTeacher?.data || []}
          />
        </Col>
      </Row>
    </>
  );
};

export default StudentsScores;
